import jsPDF from "jspdf";
import { FIRMA, BRAND_RGB } from "@/lib/branding";
import { ADMIN_TOKEN_KEY } from "@/lib/constants";

export const today = () => new Date().toISOString().slice(0, 10);

export const isDemoReadonly = () => {
  try {
    const t = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!t) return false;
    const payload = JSON.parse(atob(t.split(".")[1]));
    return !!payload.readonly;
  } catch {
    return false;
  }
};

export const plusDays = (d) => {
  const x = new Date();
  x.setDate(x.getDate() + d);
  return x.toISOString().slice(0, 10);
};

export const COUNTRY_PRESETS = {
  DE: { label: "Deutschland", currency: "EUR", rates: [19, 7, 0] },
  AT: { label: "Österreich", currency: "EUR", rates: [20, 13, 10, 0] },
  CH: { label: "Schweiz", currency: "CHF", rates: [8.1, 3.8, 2.6, 0] },
};

export const companyDocumentDefaults = (company = {}) => {
  const country = String(company.main_country || company.land || "").trim().toUpperCase();
  const isSwiss = ["CH", "SCHWEIZ", "SWITZERLAND"].includes(country)
    || String(company.base_currency || "").toUpperCase() === "CHF";
  return isSwiss
    ? { country: "CH", currency: "CHF", tax_rate: 8.1, kunde_land: "Schweiz" }
    : { country: "DE", currency: "EUR", tax_rate: 19, kunde_land: "Deutschland" };
};

export const emptyQuote = (typ = "angebot", company = {}) => ({
  dokument_typ: typ,
  anrede: "Frau",
  kunde_name: "",
  kunde_firma: "",
  kunde_strasse: "",
  kunde_plz_ort: "",
  kunde_land: companyDocumentDefaults(company).kunde_land,
  kunde_email: "",
  kunde_telefon: "",
  kunde_ustid: "",
  kunde_einvoice_endpoint: "",
  kunde_einvoice_endpoint_scheme: "",
  kunde_buyer_reference: "",
  angebot_nr: "",
  angebot_datum: today(),
  gueltig_bis: plusDays(14),
  leistungsdatum: "Nach Auftragserteilung",
  leistung_titel: "",
  leistung_beschreibung: "",
  einleitung:
    "vielen Dank für Ihre Anfrage. Nachfolgend erhalten Sie unser unverbindliches Angebot.",
  anzahl_bewertungen: 1,
  preis_pro_bewertung: 0,
  positions: [],
  include_vertrag: false,
  country: companyDocumentDefaults(company).country,
  currency: companyDocumentDefaults(company).currency,
  tax_rate: companyDocumentDefaults(company).tax_rate,
  deposit_type: "percent",
  deposit_value: 0,
  zahlungsziel: "sofort",
  reseller_link: "",
  notizen: "",
  attachments: [],
  deal_id: "",
});

// Erzeugt einen kurzen Standard-Dienstleistungsvertrag (jsPDF-Bytes).
// Wird hinten an das Angebots-PDF angehängt, wenn quote.include_vertrag = true.
export const buildVertragPdfBytes = (quote, total) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 20;
  let y = margin;

  const ensure = (need) => {
    if (y + need > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };
  const heading = (text) => {
    ensure(10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(BRAND_RGB[0], BRAND_RGB[1], BRAND_RGB[2]);
    doc.text(text, margin, y);
    y += 6;
  };
  const para = (text, opts = {}) => {
    const { size = 10, bold = false, color = [31, 31, 31] } = opts;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, pageW - margin * 2);
    ensure(lines.length * (size * 0.42) + 2);
    doc.text(lines, margin, y);
    y += lines.length * (size * 0.42) + 2;
  };
  const gap = (mm = 3) => {
    y += mm;
  };

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(BRAND_RGB[0], BRAND_RGB[1], BRAND_RGB[2]);
  doc.text("Dienstleistungsvertrag", pageW / 2, y, { align: "center" });
  y += 11;
  doc.setFontSize(10);
  doc.setTextColor(138, 111, 93);
  doc.setFont("helvetica", "normal");
  const d = quote.angebot_datum
    ? new Date(quote.angebot_datum).toLocaleDateString("de-DE")
    : "";
  doc.text(
    `Bezugsangebot: ${quote.angebot_nr || "—"}   ·   Datum: ${d}`,
    pageW / 2,
    y,
    { align: "center" }
  );
  y += 8;
  // separator
  doc.setDrawColor(200, 144, 122);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
  y += 6;

  // § 1
  heading("§ 1 Vertragsparteien");
  para("Auftragnehmerin:", { bold: true });
  para(
    `${FIRMA.name}\n${FIRMA.firma}\n${FIRMA.strasse}\n${FIRMA.plz_ort}\n${FIRMA.land}\nE-Mail: ${FIRMA.email}   ·   Tel.: ${FIRMA.telefon}`
  );
  gap(2);
  para("Auftraggeber/in:", { bold: true });
  const kundeLines = [
    `${quote.anrede || ""} ${quote.kunde_name || "—"}`.trim(),
    quote.kunde_firma || "",
    quote.kunde_strasse || "",
    quote.kunde_plz_ort || "",
    quote.kunde_land || "Deutschland",
    quote.kunde_mail ? `E-Mail: ${quote.kunde_mail}` : "",
    quote.kunde_telefon ? `Tel.: ${quote.kunde_telefon}` : "",
  ].filter(Boolean);
  para(kundeLines.join("\n"));
  gap(3);

  // § 2
  heading("§ 2 Vertragsgegenstand");
  const positions = Array.isArray(quote.positions) ? quote.positions : [];
  const sel = positions.filter((p) => p.include_in_total);
  if (sel.length > 0) {
    para(
      "Die Auftragnehmerin erbringt für die Auftraggeber/in folgende Leistungen:"
    );
    sel.forEach((p, i) => {
      para(
        `   ${i + 1}. ${p.titel}${p.einheit ? " (" + p.einheit.trim() + ")" : ""}`
      );
    });
    gap(1);
    para(
      `Der detaillierte Leistungsumfang ergibt sich aus dem zugrunde liegenden Angebot Nr. ${quote.angebot_nr || "—"}, das Bestandteil dieses Vertrages ist.`
    );
  } else {
    para(
      `Die Auftragnehmerin erbringt für die Auftraggeber/in die Leistung "${quote.leistung_titel || ""}". Der detaillierte Leistungsumfang ergibt sich aus dem zugrunde liegenden Angebot Nr. ${quote.angebot_nr || "—"}, das Bestandteil dieses Vertrages ist.`
    );
  }
  gap(3);

  // § 3
  heading("§ 3 Vergütung");
  const totalStr =
    total > 0
      ? `Die Vergütung beträgt ${total.toFixed(2).replace(".", ",")} EUR gemäß Angebot. Zahlungsmodalitäten richten sich nach den im Angebot angegebenen Zahlungsbedingungen.`
      : `Die Vergütung richtet sich nach den im Angebot Nr. ${quote.angebot_nr || "—"} angegebenen Einzelpreisen pro Leistung. Es wird ausschließlich die tatsächlich abgerufene Leistung in Rechnung gestellt.`;
  para(totalStr);
  gap(3);

  // § 4
  heading("§ 4 Laufzeit");
  para(
    "Dieser Vertrag tritt mit beidseitiger Unterzeichnung in Kraft und läuft auf unbestimmte Zeit. Einmalleistungen gelten mit Abnahme der Leistung als beendet."
  );
  gap(3);

  // § 5
  heading("§ 5 Kündigung");
  para(
    "Beide Parteien können diesen Vertrag mit einer Frist von vier (4) Wochen zum Monatsende in Textform (z.B. per E-Mail) kündigen. Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt."
  );
  gap(3);

  // § 6
  heading("§ 6 Vertraulichkeit");
  para(
    "Beide Parteien verpflichten sich, alle im Rahmen der Zusammenarbeit erlangten geschäftlichen Informationen vertraulich zu behandeln und nicht an Dritte weiterzugeben. Diese Pflicht gilt auch nach Beendigung des Vertrages fort."
  );
  gap(3);

  // § 7
  heading("§ 7 Schlussbestimmungen");
  para(
    "Änderungen und Ergänzungen dieses Vertrages bedürfen der Textform. Sollte eine Bestimmung dieses Vertrages unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt. Es gilt das Recht der Bundesrepublik Deutschland."
  );
  gap(10);

  // Signatures
  ensure(35);
  const sigY = y + 14;
  doc.setDrawColor(200, 144, 122);
  doc.setLineWidth(0.4);
  doc.line(margin, sigY, margin + 70, sigY);
  doc.line(pageW - margin - 70, sigY, pageW - margin, sigY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(138, 111, 93);
  doc.text("Ort, Datum / Auftragnehmerin", margin, sigY + 5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(31, 31, 31);
  doc.text(FIRMA.name, margin, sigY + 11);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(138, 111, 93);
  doc.text("Ort, Datum / Auftraggeber/in", pageW - margin - 70, sigY + 5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(31, 31, 31);
  doc.text(quote.kunde_name || "—", pageW - margin - 70, sigY + 11);

  return doc.output("arraybuffer");
};
